<template>
  <v-btn
    :href="cta.link || '#'"
    :class="`lightning-model-page__cta cursor-pointer`"
    :size="$vuetify.display.mobile ? 'large' : 'default'"
    :style="customStyle"
  >
    <span>{{ cta.label }}</span>
    <v-icon>mdi-chevron-right</v-icon>
  </v-btn>
</template>

<script setup lang="ts">
import { computed, defineProps, ref } from 'vue'
import type { ICallToAction } from '@jazel/jazel-vuetify'
import { getBorderCSS, getSizeAndUnitCSS } from '@/utils'

const props = defineProps<{
  settings: ICallToAction
}>()

const cta = ref<ICallToAction>(props.settings ?? {})
const ctaBorder = cta.value?.border ?? {}
const ctaColors = cta.value?.colors ?? {}
const ctaIcon = cta.value?.icon ?? {}
const ctaTypography = cta.value?.typography ?? {}

// Primary CTA font-size
const ctaFontSize = computed(() => {
  return getSizeAndUnitCSS(
    'fontSize',
    ctaTypography ?? {
      fontSize: '16',
      fontSizeUnit: 'px',
    },
  )
})

// Primary CTA letter-spacing
const ctaLetterSpacing = computed(() => {
  return getSizeAndUnitCSS(
    'letterSpacing',
    ctaTypography ?? {
      letterSpacing: '1.5',
      letterSpacingUnit: 'em',
    },
  )
})

// Primary CTA line-height
const ctaLineHeight = computed(() => {
  return getSizeAndUnitCSS(
    'lineHeight',
    ctaTypography ?? {
      lineHeight: '1.5',
      lineHeightUnit: 'em',
    },
  )
})

// Primary CTA icon container size
const ctaIconContainerSize = computed(() => {
  return getSizeAndUnitCSS(
    'container',
    ctaTypography?.sizes ?? {
      container: '30',
      containerUnit: 'px',
    },
  )
})

// Primary CTA icon size
const ctaIconSize = computed(() => {
  return getSizeAndUnitCSS(
    'icon',
    ctaIcon?.sizes ?? {
      container: '30',
      containerUnit: 'px',
    },
  )
})

// Primary CTA border styles
const ctaBorderStyle = computed(() => {
  return getBorderCSS(
    ctaBorder ?? {
      width: '1',
      widthUnit: 'px',
      style: 'solid',
      color: '#000000',
    },
  )
})

// Primary CTA border hover color
const ctaBorderHoverColor = computed(() => ctaBorder?.hoverColor ?? '#000000')

// Primary CTA border radius
const ctaBorderRadius = computed(() => {
  return getSizeAndUnitCSS(
    'radius',
    ctaBorder ?? { radius: '0', radiusUnit: 'px' },
  )
})

// Create a style object with all the custom styles
const customStyle = computed(() => {
  return {
    '--cta-bg-color': ctaColors?.background,
    '--cta-color': ctaColors?.color,
    '--cta-border': ctaBorderStyle.value,
    '--cta-border-radius': ctaBorderRadius.value,
    '--cta-font-family': ctaTypography?.fontFamily,
    '--cta-font-size': ctaFontSize.value,
    '--cta-font-style': ctaTypography?.fontStyle,
    '--cta-font-weight': ctaTypography?.fontWeight,
    '--cta-letter-spacing': ctaLetterSpacing.value,
    '--cta-text-decoration': ctaTypography?.textDecoration,
    '--cta-text-transform': ctaTypography?.textTransform,
    '--cta-line-height': ctaLineHeight.value,
    '--cta-bg-hover': ctaColors?.backgroundHover,
    '--cta-color-hover': ctaColors?.colorHover,
    '--cta-border-hover': ctaBorderHoverColor.value,
    '--cta-icon-bg': ctaIcon?.colors?.background,
    '--cta-icon-color': ctaIcon?.colors?.color,
    '--cta-icon-size': ctaIconContainerSize.value,
    '--cta-icon-font-size': ctaIconSize.value,
    '--cta-icon-bg-hover': ctaIcon?.colors?.backgroundHover,
    '--cta-icon-color-hover': ctaIcon?.colors?.colorHover,
  }
})
</script>

<style scoped>
.lightning-model-page__cta {
  background-color: var(--cta-bg-color);
  border: var(--cta-border);
  border-radius: var(--cta-border-radius);
  color: var(--cta-color);
  width: 100%;
  overflow: hidden;
}

.lightning-model-page__cta :deep(.v-btn__content span) {
  font-family: var(--cta-font-family);
  font-size: var(--cta-font-size);
  font-style: var(--cta-font-style);
  font-weight: var(--cta-font-weight);
  letter-spacing: var(--cta-letter-spacing);
  text-decoration: var(--cta-text-decoration);
  text-transform: var(--cta-text-transform);
  line-height: var(--cta-line-height);

  transition-property: all;
  transition-duration: 0.28s;
  transition-delay: unset;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.lightning-model-page__cta:hover {
  background-color: var(--cta-bg-hover);
  color: var(--cta-color-hover);
  border-color: var(--cta-border-hover);
}

.lightning-model-page__cta :deep(.v-icon) {
  background-color: var(--cta-icon-bg);
  color: var(--cta-icon-color);
  height: var(--cta-icon-size);
  margin-left: 6px;
  width: var(--cta-icon-size);

  transition-property: all;
  transition-duration: 0.28s;
  transition-delay: unset;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.lightning-model-page__cta:hover :deep(.v-icon) {
  background-color: var(--cta-icon-bg-hover);
  color: var(--cta-icon-color-hover);
}

.lightning-model-page__cta :deep(.v-icon):before {
  font-size: var(--cta-icon-font-size);
}
</style>
